import { Button, Dropdown, Space, Tooltip } from "antd";
import "./AppHeader.scss";
import Logo from "./Logo";
import CartIcon from "./icons/CartIcon";
import { useRecoilState } from "recoil";
import { memberState } from "../state/atoms";
import UserBadge from "./UserBadge";
import TemplatesMenu from "./TemplatesMenu";
import DigitalMenu from "./DigitalMenu";
import SearchCategories from "./SearchCategories";
import { NavLink, useLocation } from "react-router-dom";
import { env } from "../env/env";
import GearIcon from "./icons/GearIcon";
import { LocationDto, MemberDto } from "../generated/dashboard-client";
import React, { useContext, useEffect, useRef, useState } from "react";
import {isDSAPath, isLandingPath} from "../util/utils";
import MobileAppHeader from "./mobile/MobileAppHeader";
import OrdersApi from "../api/OrdersApi";
import { AppContext } from "../AppContext";
import {logout} from "../service/AuthService";

export interface AppHeaderProps {
  activeLocation: LocationDto;
  cartCount: number;
  setCartCount: any;
  isMarketingBannerPage: boolean;
  users: MemberDto[]
}

enum HeaderMode {
  Visible,
  Hidden,
}

export default function AppHeader({ activeLocation, cartCount, setCartCount, isMarketingBannerPage, users }: AppHeaderProps) {
  const loc = useLocation();
  const userDropdownButton = useRef<HTMLDivElement>(null);

  const { location } = useContext(AppContext);
  const [member] = useRecoilState(memberState);
  const [mode, setMode] = useState(HeaderMode.Visible);

  useEffect(() => {
    const { pathname } = loc;
    if (isLandingPath(pathname) || isDSAPath(pathname)) {
      setMode(HeaderMode.Hidden);
    } else {
      setMode(HeaderMode.Visible);
    }
  }, [loc]);

  useEffect(() => {
    // console.log("isMarketingBannerPage: " + isMarketingBannerPage);
  }, [isMarketingBannerPage]);

  useEffect(() => {
    if (!member.oldLoginKey || !location || !location.internalId) return;

    OrdersApi.getDraftItemsCount(location.internalId!).then((resp) => {
      setCartCount(resp.data);
    });
  }, [member, location, setCartCount])

  function hideUserDropdown() {
    userDropdownButton.current?.click();
  }

  const account = (
    <div className={"act-sub-menu"} onClick={hideUserDropdown}>
      <header>
        <UserBadge name={member.name} />
        <section>
          <h5>{member.name}</h5>
          <h6>{member.email}</h6>
        </section>
      </header>
      <ul>
        <li>
          <NavLink to={env.dashboardPath + "/account/users"}>
            Account Settings
          </NavLink>
        </li>
        {member.owner && !member.clover && <li>
          <NavLink to={env.dashboardPath + "/account/team"}>My Team</NavLink>
        </li>}
        {member.owner && !member.clover && <li>
          <NavLink to={env.dashboardPath + "/account/locations"}>
            My Locations
          </NavLink>
        </li>}
        <div className={"sep"} />
        <li>
          <a href={"/feature/menu-services.html"}>Menu &#38; Design Services</a>
        </li>
        <div className={"sep"} />
        <li>
          <a target="_blank" rel="noreferrer" href={"https://intercom.help/musthavemenus-289b0117ae34/en"}>Help Center</a>
        </li>
        <li>
          <a href={"/menu/contact.do"}>Contact Us</a>
        </li>
        <div className={"sep"} />
        <li>
          <a href={"/menu/logoff.do"} onClick={logout}>Sign Out</a>
        </li>
      </ul>
    </div>
  );

  function onTemplatesClick() {
    window.location.href = "/menu/browse.do";
  }

  function onDigitalClick() {
    window.location.href = "/feature/digital-menu-board-solution.html";
  }

  return (
    mode === HeaderMode.Hidden ? null : (
      <>
        <header className={"main"} style={{top: !isMarketingBannerPage ? '0' : ''}}>
          <Logo activeLocation={activeLocation} />
          <nav className={`links-nav ${!member.id ? 'logged-out' : ''}`}>
            <ul>
              {member.id &&
                <li>
                  <NavLink
                    className={
                      "ant-btn ant-btn-link ant-btn-lg" +
                      (window.location.pathname.includes("home") &&
                        !window.location.pathname.includes("account")
                        ? " active"
                        : "")
                    }
                    to={`${env.dashboardPath}/designs`}
                  >
                    Home
                  </NavLink>
                </li>}
              <li>
                <Dropdown overlay={<TemplatesMenu />} placement="bottomRight" autoAdjustOverflow={true}>
                  <Button type={"link"} size={"large"} onClick={onTemplatesClick}>
                    Templates
                  </Button>
                </Dropdown>
              </li>
              <li>
                <Dropdown
                  overlay={
                    <div>
                      <DigitalMenu />
                    </div>
                  }
                  placement="bottomRight"
                >
                  <Button type={"link"} size={"large"} onClick={onDigitalClick}>
                    Digital
                  </Button>
                </Dropdown>
              </li>
              {!member.id &&
                <li>
                  <Button href={"/feature/explore.html"} type={"link"} size={"large"}>
                    Explore
                  </Button>
                </li>}
              {!member.id &&
                <li>
                  <Dropdown
                    overlay={
                      <div className={"pricing-dropdown"}>
                        <ul>
                          <li>
                            <a href={"/menu/pricing.do"}>
                              Low-Cost Pricing Plans
                            </a>
                          </li>
                          <li>
                            <a href={"/feature/menu-services.html"}>
                              Menu Services
                            </a>
                          </li>
                          <li>
                            <a href={"/feature/musthavemenus-vs-competitors.html"}>
                              MustHaveMenus vs Competitors
                            </a>
                          </li>
                        </ul>
                      </div>
                    }
                  >
                    <Button type={"link"} size={"large"}>
                      Pricing
                    </Button>
                  </Dropdown>
                </li>
              }
            </ul>
            <div>
              <Button className={"demo-btn"} href={member.id ? '/feature/book-demo-auth.html' : '/feature/book-demo-hello.html'} target={"_blank"} type={"primary"} size={"large"}>
                Book a Demo
              </Button>
            </div>
          </nav>

          <aside>
            <Space>
              <SearchCategories />
            </Space>

            {member.id && <nav>
              <ul>
                <li>
                  <Tooltip placement="bottom" title={"Settings"}>
                    <NavLink
                      className={"ant-btn ant-btn-link settings"}
                      to={env.dashboardPath + "/account/users"}
                    >
                      <GearIcon />
                    </NavLink>
                  </Tooltip>
                </li>
                <li>
                  <Tooltip placement="bottom" title={"Print Cart"}>
                    <NavLink className={"ant-btn ant-btn-link cart"} to={env.dashboardPath + "/print/checkout"}>
                      <CartIcon />
                      {(cartCount > 0) && <span className={"cart-count"}>{cartCount}</span>}
                    </NavLink>
                  </Tooltip>
                </li>
              </ul>
              <Dropdown overlay={account} trigger={["click"]} getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}>
                <Button type={"link"} className={"bdg"} ref={userDropdownButton}>
                  <UserBadge name={member.name} />
                </Button>
              </Dropdown>
            </nav>}

            {!member.id && <nav>
              <Button className={"login-btn"} style={{ marginLeft: "10px" }}
                onClick={() => window.location.href = "/account/login"}>Log In</Button>
              <Button className={"join-btn"} style={{ marginLeft: "8px" }}
                onClick={() => window.location.href = "/account/register"}>Join</Button>
            </nav>}
          </aside>
        </header>

        <MobileAppHeader activeLocation={activeLocation} cartCount={cartCount} setCartCount={setCartCount} users={users} />
      </>
    )
  );
}
